// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

// eslint-disable-next-line @nx/enforce-module-boundaries -- documents page LoaderStatus; cycle with aggregation drawers using EntityDocumentsSection
import { type LoaderStatus } from '@shared/advisory/aggregation/components/loader';
import { documentsApi } from '@shared/data-access/api/engine/documents-api';
import type { Document } from '@shared/data-access/types/engine-types';
import type { DocumentScope } from '@shared/data-access/types/engine-types';
import { useDebounce } from '@shared/ui-kit/hooks/use-debounce';
import { useCallback, useEffect, useMemo, useState, type ChangeEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useDeleteDocumentConfirm } from '../../hooks/use-delete-document-confirm';
import { useDocuments } from '../../hooks/use-documents';
import {
  countActiveDrawerFilters,
  defaultDocumentFiltersState,
  type DocumentDrawerFilters,
  type DocumentFiltersState,
  type DocumentScopeTab,
} from '../../types/document-filters';
import { downloadDocumentFile } from '../../utils/download-document-files';
import { filterDocuments } from '../../utils/filter-documents';

const MOBILE_DOCUMENTS_BATCH_SIZE = 5;

export type DocumentsPageProps = {
  readonly lockedScope?: DocumentScope;
  readonly embedded?: boolean;
  readonly hideUpload?: boolean;
  readonly hideScopeTabs?: boolean;
};

export const useDocumentsPage = ({
  lockedScope,
  embedded = false,
  hideUpload = false,
  hideScopeTabs = false,
}: DocumentsPageProps = {}) => {
  const { t } = useTranslation();
  const { documents, documentsQuery, refreshDocuments } = useDocuments();
  const [fetchDocumentDetail] = documentsApi.useFetchDocumentDetailMutation();

  const [filters, setFilters] = useState<DocumentFiltersState>(() => ({
    ...defaultDocumentFiltersState(),
    ...(lockedScope ? { scope: lockedScope } : {}),
  }));
  const [filtersDrawerOpen, setFiltersDrawerOpen] = useState(false);
  const [detailDrawerOpen, setDetailDrawerOpen] = useState(false);
  const [uploadDrawerOpen, setUploadDrawerOpen] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState<Document | null>(null);
  const debouncedSearch = useDebounce(filters.search, 300);

  const filteredDocuments = useMemo(
    () => filterDocuments(documents, { ...filters, search: debouncedSearch }),
    [documents, debouncedSearch, filters],
  );

  const [mobileVisibleCount, setMobileVisibleCount] = useState(
    MOBILE_DOCUMENTS_BATCH_SIZE,
  );

  useEffect(() => {
    setMobileVisibleCount(MOBILE_DOCUMENTS_BATCH_SIZE);
  }, [debouncedSearch, filters, filteredDocuments]);

  const mobileVisibleDocuments = useMemo(
    () => filteredDocuments.slice(0, mobileVisibleCount),
    [filteredDocuments, mobileVisibleCount],
  );

  const hasMoreMobileDocuments = mobileVisibleCount < filteredDocuments.length;

  const onLoadMoreMobileDocuments = useCallback(() => {
    setMobileVisibleCount(current => current + MOBILE_DOCUMENTS_BATCH_SIZE);
  }, []);

  const drawerFilters = useMemo(() => {
    const { scope: _scope, search: _search, ...rest } = filters;
    return rest;
  }, [filters]);

  const activeFilterCount = useMemo(
    () => countActiveDrawerFilters(drawerFilters),
    [drawerFilters],
  );

  const status = useMemo<LoaderStatus>(() => {
    const hasCachedDocuments = documents.length > 0;

    if (documentsQuery.isLoading && !hasCachedDocuments) {
      return 'pending';
    }

    if (documentsQuery.isError && !hasCachedDocuments) {
      return 'rejected';
    }

    if (documentsQuery.isFetching) {
      return 'fetching';
    }

    return 'fulfilled';
  }, [
    documents.length,
    documentsQuery.isError,
    documentsQuery.isFetching,
    documentsQuery.isLoading,
  ]);

  const onSearchChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    setFilters(current => ({
      ...current,
      search: event.target.value,
    }));
  }, []);

  const onScopeChange = useCallback(
    (scope: DocumentScopeTab) => {
      if (lockedScope) {
        return;
      }

      setFilters(current => ({
        ...current,
        scope,
        category: '',
        subcategory: '',
      }));
    },
    [lockedScope],
  );

  const onOpenFilters = useCallback(() => {
    setFiltersDrawerOpen(true);
  }, []);

  const onCloseFilters = useCallback(() => {
    setFiltersDrawerOpen(false);
  }, []);

  const onApplyDrawerFilters = useCallback(
    (filtersToApply: DocumentDrawerFilters) => {
      setFilters(current => ({
        ...current,
        ...filtersToApply,
      }));
    },
    [],
  );

  const onClearAllFilters = useCallback(() => {
    setFilters({
      ...defaultDocumentFiltersState(),
      ...(lockedScope ? { scope: lockedScope } : {}),
    });
  }, [lockedScope]);

  const onRemoveDrawerFilter = useCallback((key: keyof DocumentDrawerFilters) => {
    setFilters(current => ({
      ...current,
      [key]: '',
    }));
  }, []);

  const onUpload = useCallback(() => {
    if (hideUpload) {
      return;
    }

    setUploadDrawerOpen(true);
  }, [hideUpload]);

  const onCloseUploadDrawer = useCallback(() => {
    setUploadDrawerOpen(false);
  }, []);

  const onRefresh = refreshDocuments;

  const onViewDetailDocument = useCallback((document: Document) => {
    setSelectedDocument(document);
    setDetailDrawerOpen(true);
  }, []);

  const onDownloadDocument = useCallback(
    async (document: Document) => {
      const fileCount = document.files?.length ?? 0;

      if (fileCount > 1) {
        onViewDetailDocument(document);
        return;
      }

      try {
        const detail = await fetchDocumentDetail({
          document_id: document.id,
        }).unwrap();

        if (detail.files.length === 1) {
          downloadDocumentFile(detail.files[0].url, detail.files[0].file_name);
          return;
        }

        if (detail.files.length > 1) {
          onViewDetailDocument(document);
        }
      } catch {
        // Signed URLs are only available from GET /documents/{id}/.
      }
    },
    [fetchDocumentDetail, onViewDetailDocument],
  );

  const onCloseDetailDrawer = useCallback(() => {
    setDetailDrawerOpen(false);
    setSelectedDocument(null);
  }, []);

  const { confirmDeleteDocument } = useDeleteDocumentConfirm();

  const onDeleteDocument = useCallback(
    (document: Document) => {
      confirmDeleteDocument(document);
    },
    [confirmDeleteDocument],
  );

  return {
    t,
    allDocumentsCount: documents.length,
    filteredDocuments,
    mobileVisibleDocuments,
    hasMoreMobileDocuments,
    onLoadMoreMobileDocuments,
    status,
    search: filters.search,
    scope: filters.scope,
    activeFilterCount,
    drawerFilters,
    filtersDrawerOpen,
    onSearchChange,
    onScopeChange,
    onOpenFilters,
    onCloseFilters,
    onApplyDrawerFilters,
    onClearAllFilters,
    onRemoveDrawerFilter,
    onUpload,
    onCloseUploadDrawer,
    uploadDrawerOpen,
    onRefresh,
    onDownloadDocument,
    onViewDetailDocument,
    onDeleteDocument,
    detailDrawerOpen,
    selectedDocument,
    onCloseDetailDrawer,
    hasData: filteredDocuments.length > 0,
    isToolbarDisabled: status === 'pending' || status === 'fetching',
    embedded,
    hideUpload,
    hideScopeTabs: hideScopeTabs || Boolean(lockedScope),
  };
};
